import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Property } from '../../../shared/types/Property';
import { BuildingIcon, TagIcon, MapPinIcon, PhoneIcon, GlobeIcon, ClockIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
export function PropertyInformation() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data for a Malaysian island resort
  const [property, setProperty] = useState<Property>({
    name: 'Baa Atoll Lagoon Paradise Resort',
    description: 'Nestled on the pristine shores of Langkawi, Langkawi Lagoon Paradise Resort offers a perfect blend of Malaysian luxury and natural beauty. Surrounded by turquoise lagoons and overlooking the crystal-clear waters of the Andaman Sea, this exclusive retreat provides an authentic Malaysian experience with modern comforts.',
    designation: 'Resort',
    starRating: 5,
    tags: ['Overwater Villas', 'Spa Retreat', 'Family-Friendly', 'Luxury', 'Malaysian Cuisine', 'Beachfront', 'Island Escape', 'Wellness-Retreat', 'All-Inclusive', 'Yoga Classes', 'Eco-Friendly', 'Cultural Experiences'],
    address: {
      office: 'Eydhafushi Island',
      city: 'Baa Atoll',
      postalCode: '06010',
      country: 'Malaysia',
      latitude: '5.1042',
      longitude: '73.0680'
    },
    contact: {
      email: 'reservations@baaatolllagoon.mv',
      phone: '+960 660 1234',
      website: 'https://www.baaatolllagoonresort.mv',
      timezone: 'Asia/Kuala_Lumpur'
    },
    checkIn: '14:00',
    checkOut: '12:00',
    amenities: ['Infinity Pool', 'Private Pool Villas', 'WiFi', 'Airport Shuttle', 'Daily Housekeeping', 'Smart TV', 'Hair Dryer', 'In-Room Safe', 'Bicycle Rental', 'Malaysian Cooking Classes', 'Sunset Cruise', 'Island Hopping', 'Floating Breakfast', 'Traditional Malaysian Massage', 'Bodu Beru Performances', 'Dolphin Cruise Tours'],
    transportation: ['Taxi', 'Hotel Shuttle', 'Motorbike Rental', 'Longtail Boat', 'Ferry', 'Domestic Flight', 'Private Van Transfer']
  });
  // Function to render tags
  const renderTags = (tags: string[]) => {
    return <div className="flex flex-wrap gap-2" data-id="element-9658">
        {tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-9659">
            {tag}
            <button aria-label={`Remove ${tag}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-9660">
              <XIcon size={12} aria-hidden="true" data-id="element-9661" />
            </button>
          </div>)}
      </div>;
  };
  // Draft information tooltip content
  const draftInfoContent = "Your hotel listing is currently in draft mode. It won't be visible until your booking engine is enabled. Once you add availability or offers through your inventory management, our team will review and publish them when ready.";
  return <div className="space-y-6" data-id="element-9662">
      {/* Simplified header section */}
      <div data-id="element-9663">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4" data-id="element-9664">
          {t('pages.PropertyInformation.hotel')}
        </h1>
        <div className="flex justify-between items-center" data-id="element-9665">
          {/* Left side - tabs */}
          <div className="border-b border-gray-200" data-id="element-9666">
            <div className="flex space-x-6" data-id="element-9667">
              <button className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-2" data-id="element-9668">
                {t('pages.PropertyInformation.hotel_information')}
              </button>
              <button className="text-sm font-medium text-gray-500 hover:text-gray-800 pb-2" data-id="element-9669">
                {t('components.EditRoomForm.media_assets')}
              </button>
            </div>
          </div>
          {/* Right side - status button with tooltip */}
          <div className="relative group" data-id="element-9670">
            <button className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium" data-id="element-9671">
              <span className="mr-2" data-id="element-9672">
                {t('pages.PropertyInformation.draft_submitted_for_review')}
              </span>
              <InfoIcon size={16} className="text-gray-500" data-id="element-9673" />
            </button>
            <div className="absolute right-0 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block" data-id="element-9674">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-9675">
                {t('pages.PropertyInformation.draft_status')}
              </h3>
              <p className="text-xs text-gray-600" data-id="element-9676">{draftInfoContent}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Hotel Details Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-9677">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9678">
          <div data-id="element-9679">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9680">
              {t('pages.PropertyInformation.hotel_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.name} onChange={e => setProperty({
            ...property,
            name: e.target.value
          })} data-id="element-9681" />
          </div>
          <div data-id="element-9682">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9683">
              {t('pages.PropertyInformation.designation')}
            </label>
            <div className="relative" data-id="element-9684">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.designation} onChange={e => setProperty({
              ...property,
              designation: e.target.value
            })} data-id="element-9685">
                <option value="Resort" data-id="element-9686">
                  {t('pages.PropertyInformation.resort')}
                </option>
                <option value="Hotel" data-id="element-9687">
                  {t('pages.PropertyInformation.hotel')}
                </option>
                <option value="Villa" data-id="element-9688">
                  {t('pages.PropertyInformation.villa')}
                </option>
                <option value="Guesthouse" data-id="element-9689">
                  {t('pages.PropertyInformation.guesthouse')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9690">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9691" />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-9692">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9693">
            {t('components.AddRoomForm.description')}
          </label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 h-24" value={property.description} onChange={e => setProperty({
          ...property,
          description: e.target.value
        })} data-id="element-9694" />
        </div>
        <div className="mb-6" data-id="element-9695">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9696">
            {t('pages.PropertyInformation.star_rating')}
          </label>
          <div className="relative" data-id="element-9697">
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.starRating} onChange={e => setProperty({
            ...property,
            starRating: parseInt(e.target.value)
          })} data-id="element-9698">
              <option value="5" data-id="element-9699">5</option>
              <option value="4" data-id="element-9700">4</option>
              <option value="3" data-id="element-9701">3</option>
              <option value="2" data-id="element-9702">2</option>
              <option value="1" data-id="element-9703">1</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9704">
              <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9705" />
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-9706">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9707">
            {t('pages.PropertyInformation.tags')}
          </label>
          {renderTags(property.tags)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9708">
          <div data-id="element-9709">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9710">
              {t('pages.PropertyInformation.address')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.office} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              office: e.target.value
            }
          })} data-id="element-9711" />
          </div>
          <div data-id="element-9712">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9713">
              {t('pages.PropertyInformation.area_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value="Pantai Cenang" data-id="element-9714" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9715">
          <div data-id="element-9716">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9717">
              {t('pages.PropertyInformation.city')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.city} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              city: e.target.value
            }
          })} data-id="element-9718" />
          </div>
          <div data-id="element-9719">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9720">
              {t('pages.PropertyInformation.postal_code')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.postalCode} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              postalCode: e.target.value
            }
          })} data-id="element-9721" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-9722">
          <div data-id="element-9723">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9724">
              {t('pages.PropertyInformation.country')}
            </label>
            <div className="relative" data-id="element-9725">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.address.country} onChange={e => setProperty({
              ...property,
              address: {
                ...property.address,
                country: e.target.value
              }
            })} data-id="element-9726">
                <option value="Malaysia" data-id="element-9727">
                  {t('pages.PropertyInformation.malaysia')}
                </option>
                <option value="Sri Lanka" data-id="element-9728">
                  {t('pages.PropertyInformation.indonesia')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9729">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9730" />
              </div>
            </div>
          </div>
          <div data-id="element-9731">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9732">
              {t('pages.PropertyInformation.latitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.latitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              latitude: e.target.value
            }
          })} data-id="element-9733" />
          </div>
          <div data-id="element-9734">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9735">
              {t('pages.PropertyInformation.longitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.longitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              longitude: e.target.value
            }
          })} data-id="element-9736" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9737">
          <div data-id="element-9738">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9739">
              {t('pages.PropertyInformation.contact_email')}
            </label>
            <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.email} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              email: e.target.value
            }
          })} data-id="element-9740" />
          </div>
          <div data-id="element-9741">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9742">
              {t('pages.PropertyInformation.phone_number')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.phone} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              phone: e.target.value
            }
          })} data-id="element-9743" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9744">
          <div data-id="element-9745">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9746">
              {t('pages.PropertyInformation.website')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.contact.website} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              website: e.target.value
            }
          })} data-id="element-9747" />
          </div>
          <div data-id="element-9748">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9749">
              {t('pages.PropertyInformation.timezone')}
            </label>
            <div className="relative" data-id="element-9750">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.contact.timezone} onChange={e => setProperty({
              ...property,
              contact: {
                ...property.contact,
                timezone: e.target.value
              }
            })} data-id="element-9751">
                <option value="Asia/Kuala_Lumpur" data-id="element-9752">
                  {t('pages.PropertyInformation.asiakualalumpur')}
                </option>
                <option value="Asia/Singapore" data-id="element-9753">
                  {t('pages.PropertyInformation.asiasingapore')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9754">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9755" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9756">
          <div data-id="element-9757">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9758">
              {t('pages.PropertyInformation.checkin_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkIn} onChange={e => setProperty({
            ...property,
            checkIn: e.target.value
          })} data-id="element-9759" />
          </div>
          <div data-id="element-9760">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9761">
              {t('pages.PropertyInformation.checkout_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkOut} onChange={e => setProperty({
            ...property,
            checkOut: e.target.value
          })} data-id="element-9762" />
          </div>
        </div>
        <div className="mb-6" data-id="element-9763">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9764">
            {t('pages.PropertyInformation.hotel_amenities')}
          </label>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-9765">
            <div className="flex flex-wrap gap-2" data-id="element-9766">
              {property.amenities.map((amenity, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-9767">
                  {amenity}
                  <button aria-label={`Remove ${amenity}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-9768">
                    <XIcon size={12} aria-hidden="true" data-id="element-9769" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        <div data-id="element-9770">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9771">
            {t('pages.PropertyInformation.transportation')}
          </label>
          <div className="space-y-2" data-id="element-9772">
            {property.transportation.map((item, index) => <div key={index} className="flex items-center" data-id="element-9773">
                <input type="checkbox" id={`transportation-${index}`} checked={true} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-9774" />

                <label htmlFor={`transportation-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-9775">
                  {item}
                </label>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-9776">
      <polyline points="6 9 12 15 18 9" data-id="element-9777"></polyline>
    </svg>;
}